/**
 * Immagini da condividere (Andrea 2026-10-08): la formazione schierata e il
 * risultato di una partita come PNG, disegnati con il canvas (niente
 * librerie). Formato verticale 1080×1350 per la formazione (va bene anche
 * come storia/post), quadrato 1080×1080 per il risultato.
 *
 * Colori fissi della palette scura: l'immagine deve essere uguale per tutti,
 * qualunque tema abbia chi la manda.
 */

import { MODULO_LAYOUT, type Modulo } from "../formazione/pitchLayouts";

const VIOLA_NOTTE = "#1A0F2E";
const VIOLA = "#4C1D95";
const VIOLA_DEEP = "#2A0F4E";
const ORO = "#D4AF37";
const LAVANDA = "#E8DFFA";
const LAVANDA_MUTED = "#9B8AB8";
const ERBA = "#1F6B3A";
const ERBA_CHIARA = "#257A43";

const FONT = '"Montserrat", system-ui, -apple-system, "Segoe UI", sans-serif';
const MONO = '"PT Mono", ui-monospace, Menlo, monospace';

export interface GiocatoreCard {
  nome: string;
  /** sigla del ruolo nello slot (Por, Dc, W… oppure P/D/C/A) */
  ruolo: string;
  capitano?: boolean;
  vice?: boolean;
  /** icona del giocatore (vedi urlIconaGiocatore); senza, un cerchio con la sigla */
  icona?: string | null;
}

export interface DatiFormazioneCard {
  squadra: string;
  logoDataUrl?: string | null;
  modulo: Modulo;
  titolari: GiocatoreCard[]; // 11, nell'ordine degli slot del modulo
  panchina: GiocatoreCard[];
  intestazione: string; // es. "Campionato · Giornata 7"
  sottotitolo?: string; // es. "Serie A 8ª"
}

export interface SquadraRisultatoCard {
  nome: string;
  logoDataUrl?: string | null;
  gol: number;
  fantapunti: number | null;
}

export interface DatiRisultatoCard {
  intestazione: string; // es. "Champions League · Giornata 7"
  casa: SquadraRisultatoCard;
  trasferta: SquadraRisultatoCard;
  nota?: string | null; // es. "Voti live, risultato provvisorio"
}

async function fontPronti() {
  try {
    await Promise.all([
      document.fonts.load(`700 40px ${FONT}`),
      document.fonts.load(`500 30px ${FONT}`),
      document.fonts.load(`500 30px ${MONO}`),
    ]);
  } catch {
    /* si usa il font di sistema */
  }
}

function caricaImmagine(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const im = new Image();
    // nell'app Capacitor le icone arrivano da fantasquad.it: senza CORS il
    // canvas diventerebbe "sporco" e toBlob fallirebbe
    if (!src.startsWith("data:")) im.crossOrigin = "anonymous";
    im.onload = () => resolve(im);
    im.onerror = () => resolve(null);
    im.src = src;
  });
}

function iniziali(nome: string): string {
  const parti = nome.trim().split(/\s+/).filter(Boolean);
  if (parti.length === 0) return "?";
  if (parti.length === 1) return parti[0].slice(0, 2).toUpperCase();
  return (parti[0][0] + parti[parti.length - 1][0]).toUpperCase();
}

/** Logo tondo, o le iniziali su sfondo viola/oro. */
async function disegnaLogo(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  nome: string,
  logoDataUrl?: string | null,
) {
  const im = logoDataUrl ? await caricaImmagine(logoDataUrl) : null;
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.closePath();
  if (im) {
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.clip();
    // copre il cerchio mantenendo le proporzioni
    const s = Math.max((2 * r) / im.width, (2 * r) / im.height);
    const w = im.width * s;
    const h = im.height * s;
    ctx.drawImage(im, x - w / 2, y - h / 2, w, h);
  } else {
    const g = ctx.createLinearGradient(x - r, y - r, x + r, y + r);
    g.addColorStop(0, VIOLA);
    g.addColorStop(1, ORO);
    ctx.fillStyle = g;
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.font = `700 ${Math.round(r * 0.9)}px ${FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(iniziali(nome), x, y + r * 0.05);
  }
  ctx.restore();
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.lineWidth = Math.max(3, r * 0.08);
  ctx.strokeStyle = ORO;
  ctx.stroke();
}

function testoAdatta(ctx: CanvasRenderingContext2D, testo: string, maxW: number): string {
  if (ctx.measureText(testo).width <= maxW) return testo;
  let t = testo;
  while (t.length > 1 && ctx.measureText(t + "…").width > maxW) t = t.slice(0, -1);
  return t + "…";
}

function cognome(nome: string): string {
  // Nel listone i nomi sono "Cognome I." e restano cosi'. Un nome a due
  // parole senza iniziale ("Carlos Augusto") resta intero; da tre in su
  // ("Lautaro Martinez Jr") si toglie il primo.
  const parti = nome.trim().split(/\s+/);
  if (parti.length <= 2 || /^[A-ZÀ-Ý]\.$/.test(parti[parti.length - 1])) return nome.trim();
  return parti.slice(1).join(" ");
}

function sfondo(ctx: CanvasRenderingContext2D, w: number, h: number) {
  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0, VIOLA_DEEP);
  g.addColorStop(1, VIOLA_NOTTE);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
  // righe verticali sottili, come l'intestazione dell'app
  ctx.strokeStyle = "rgba(232,223,250,0.035)";
  ctx.lineWidth = 2;
  for (let x = 0; x < w; x += 60) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
    ctx.stroke();
  }
}

function piedino(ctx: CanvasRenderingContext2D, w: number, h: number) {
  ctx.fillStyle = LAVANDA_MUTED;
  ctx.font = `600 26px ${FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("fantasquad.it", w / 2, h - 36);
}

function campo(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) {
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, 28);
  ctx.clip();
  // strisce di erba
  const n = 8;
  for (let i = 0; i < n; i++) {
    ctx.fillStyle = i % 2 === 0 ? ERBA : ERBA_CHIARA;
    ctx.fillRect(x, y + (h * i) / n, w, h / n + 1);
  }
  ctx.strokeStyle = "rgba(255,255,255,0.65)";
  ctx.lineWidth = 4;
  const m = 26;
  ctx.strokeRect(x + m, y + m, w - 2 * m, h - 2 * m);
  // meta' campo e cerchio
  ctx.beginPath();
  ctx.moveTo(x + m, y + h / 2);
  ctx.lineTo(x + w - m, y + h / 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x + w / 2, y + h / 2, w * 0.13, 0, Math.PI * 2);
  ctx.stroke();
  // aree
  const aw = w * 0.5;
  const ah = h * 0.14;
  ctx.strokeRect(x + (w - aw) / 2, y + m, aw, ah);
  ctx.strokeRect(x + (w - aw) / 2, y + h - m - ah, aw, ah);
  const pw = w * 0.24;
  const ph = h * 0.055;
  ctx.strokeRect(x + (w - pw) / 2, y + m, pw, ph);
  ctx.strokeRect(x + (w - pw) / 2, y + h - m - ph, pw, ph);
  ctx.restore();
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, 28);
  ctx.lineWidth = 4;
  ctx.strokeStyle = "rgba(212,175,55,0.6)";
  ctx.stroke();
}

function giocatoreSulCampo(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  g: GiocatoreCard,
  icona: HTMLImageElement | null,
) {
  const r = icona ? 44 : 34;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  if (icona) {
    // l'icona del giocatore (Andrea 2026-10-08), ritagliata nel cerchio
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fillStyle = VIOLA_DEEP;
    ctx.fill();
    ctx.clip();
    const s = Math.max((2 * r) / icona.width, (2 * r) / icona.height);
    ctx.drawImage(
      icona,
      cx - (icona.width * s) / 2,
      cy - (icona.height * s) / 2,
      icona.width * s,
      icona.height * s,
    );
    ctx.restore();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.lineWidth = 4;
    ctx.strokeStyle = g.capitano ? ORO : "rgba(255,255,255,0.9)";
    ctx.stroke();
    // sigla del ruolo in basso a sinistra
    const sx = cx - r + 8;
    const sy = cy + r - 8;
    ctx.fillStyle = VIOLA_DEEP;
    ctx.beginPath();
    ctx.arc(sx, sy, 16, 0, Math.PI * 2);
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = "rgba(255,255,255,0.7)";
    ctx.stroke();
    ctx.fillStyle = LAVANDA;
    ctx.font = `700 ${g.ruolo.length > 2 ? 12 : 15}px ${FONT}`;
    ctx.fillText(g.ruolo, sx, sy + 1);
  } else {
    // senza icona: cerchio viola con la sigla
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = VIOLA_DEEP;
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = g.capitano ? ORO : "rgba(255,255,255,0.85)";
    ctx.stroke();
    ctx.fillStyle = g.capitano ? ORO : LAVANDA;
    ctx.font = `700 22px ${FONT}`;
    ctx.fillText(g.ruolo, cx, cy + 1);
  }
  if (g.capitano || g.vice) {
    const bx = cx + r - 6;
    const by = cy - r + 6;
    ctx.beginPath();
    ctx.arc(bx, by, 15, 0, Math.PI * 2);
    ctx.fillStyle = g.capitano ? ORO : LAVANDA;
    ctx.fill();
    ctx.fillStyle = VIOLA_DEEP;
    ctx.font = `800 17px ${FONT}`;
    ctx.fillText(g.capitano ? "C" : "V", bx, by + 1);
  }
  // nome su targhetta
  ctx.font = `700 24px ${FONT}`;
  const nome = testoAdatta(ctx, cognome(g.nome), 230);
  const tw = ctx.measureText(nome).width + 24;
  ctx.fillStyle = "rgba(26,15,46,0.85)";
  ctx.beginPath();
  ctx.roundRect(cx - tw / 2, cy + r + 8, tw, 36, 10);
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.fillText(nome, cx, cy + r + 27);
}

function aBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("canvas vuoto"))), "image/png"),
  );
}

export async function disegnaFormazione(d: DatiFormazioneCard): Promise<Blob> {
  await fontPronti();
  const W = 1080;
  const H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;
  sfondo(ctx, W, H);

  // intestazione: logo + squadra + competizione/giornata
  await disegnaLogo(ctx, 110, 110, 62, d.squadra, d.logoDataUrl);
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = ORO;
  ctx.font = `700 54px ${FONT}`;
  ctx.fillText(testoAdatta(ctx, d.squadra, 700), 200, 100);
  ctx.fillStyle = LAVANDA;
  ctx.font = `600 30px ${FONT}`;
  ctx.fillText(testoAdatta(ctx, d.intestazione, 700), 200, 146);
  if (d.sottotitolo) {
    ctx.fillStyle = LAVANDA_MUTED;
    ctx.font = `500 24px ${FONT}`;
    ctx.fillText(d.sottotitolo, 200, 180);
  }
  // modulo a destra
  ctx.textAlign = "right";
  ctx.fillStyle = ORO;
  ctx.font = `700 44px ${MONO}`;
  ctx.fillText(d.modulo, W - 60, 118);
  ctx.fillStyle = LAVANDA_MUTED;
  ctx.font = `500 22px ${FONT}`;
  ctx.fillText("MODULO", W - 60, 150);

  // campo
  const cx0 = 60;
  const cy0 = 215;
  const cw = W - 120;
  const ch = 860;
  campo(ctx, cx0, cy0, cw, ch);
  const layout = MODULO_LAYOUT[d.modulo] ?? MODULO_LAYOUT["4-3-3"];
  // prima i piu' in alto (attacco), cosi' le targhette non coprono chi sta sotto
  const icone = await Promise.all(
    d.titolari.map((g) => (g.icona ? caricaImmagine(g.icona) : Promise.resolve(null))),
  );
  const ordine = d.titolari
    .map((g, i) => ({ g, pos: layout[i], icona: icone[i] }))
    .filter((x) => x.pos)
    .sort((a, b) => a.pos.y - b.pos.y);
  for (const { g, pos, icona } of ordine) {
    const px = cx0 + (cw * pos.x) / 100;
    const py = cy0 + 40 + ((ch - 110) * pos.y) / 100;
    giocatoreSulCampo(ctx, px, py, g, icona);
  }

  // panchina
  const by = cy0 + ch + 48;
  ctx.textAlign = "left";
  ctx.fillStyle = ORO;
  ctx.font = `700 24px ${FONT}`;
  ctx.fillText("PANCHINA", 60, by);
  ctx.fillStyle = LAVANDA;
  ctx.font = `500 26px ${FONT}`;
  const voci = d.panchina.map(
    (g) => `${g.ruolo} ${cognome(g.nome)}${g.capitano ? " (C)" : g.vice ? " (V)" : ""}`,
  );
  // su due righe al massimo
  const righe: string[] = [];
  let riga = "";
  for (const v of voci) {
    const prova = riga ? `${riga} · ${v}` : v;
    if (ctx.measureText(prova).width > W - 120 && riga) {
      righe.push(riga);
      riga = v;
    } else riga = prova;
  }
  if (riga) righe.push(riga);
  righe
    .slice(0, 2)
    .forEach((r, i) => ctx.fillText(testoAdatta(ctx, r, W - 120), 60, by + 40 + i * 36));

  piedino(ctx, W, H);
  return aBlob(canvas);
}

export async function disegnaRisultato(d: DatiRisultatoCard): Promise<Blob> {
  await fontPronti();
  const W = 1080;
  const H = 1080;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;
  sfondo(ctx, W, H);

  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = ORO;
  ctx.font = `700 34px ${FONT}`;
  ctx.fillText(testoAdatta(ctx, d.intestazione.toUpperCase(), W - 120), W / 2, 120);

  // loghi ai lati, risultato grande al centro, sotto i fantapunti; i nomi
  // stanno sotto i loghi, su una riga diversa da tutto il resto.
  const yLogo = 420;
  const xCasa = 205;
  const xTrasf = W - 205;
  await disegnaLogo(ctx, xCasa, yLogo, 105, d.casa.nome, d.casa.logoDataUrl);
  await disegnaLogo(ctx, xTrasf, yLogo, 105, d.trasferta.nome, d.trasferta.logoDataUrl);
  ctx.fillStyle = LAVANDA;
  ctx.font = `700 36px ${FONT}`;
  ctx.fillText(testoAdatta(ctx, d.casa.nome, 370), xCasa, yLogo + 170);
  ctx.fillText(testoAdatta(ctx, d.trasferta.nome, 370), xTrasf, yLogo + 170);
  ctx.fillStyle = LAVANDA_MUTED;
  ctx.font = `500 22px ${FONT}`;
  ctx.fillText("CASA", xCasa, yLogo + 204);
  ctx.fillText("TRASFERTA", xTrasf, yLogo + 204);

  // risultato al centro, su una targa scura
  ctx.fillStyle = "rgba(26,15,46,0.7)";
  ctx.beginPath();
  ctx.roundRect(W / 2 - 190, yLogo - 95, 380, 170, 28);
  ctx.fill();
  ctx.strokeStyle = "rgba(212,175,55,0.5)";
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.fillStyle = "#ffffff";
  ctx.font = `800 120px ${MONO}`;
  ctx.textBaseline = "middle";
  ctx.fillText(`${d.casa.gol} - ${d.trasferta.gol}`, W / 2, yLogo - 8);
  ctx.textBaseline = "alphabetic";
  if (d.casa.fantapunti !== null && d.trasferta.fantapunti !== null) {
    ctx.fillStyle = ORO;
    ctx.font = `700 40px ${MONO}`;
    ctx.fillText(
      `${d.casa.fantapunti.toFixed(1)} · ${d.trasferta.fantapunti.toFixed(1)}`,
      W / 2,
      yLogo + 150,
    );
    ctx.fillStyle = LAVANDA_MUTED;
    ctx.font = `500 22px ${FONT}`;
    ctx.fillText("FANTAPUNTI", W / 2, yLogo + 184);
  }
  if (d.nota) {
    ctx.fillStyle = LAVANDA_MUTED;
    ctx.font = `500 26px ${FONT}`;
    ctx.fillText(testoAdatta(ctx, d.nota, W - 160), W / 2, 760);
  }
  // scritta FantaSquad
  ctx.fillStyle = ORO;
  ctx.font = `700 40px ${FONT}`;
  ctx.fillText("FANTASQUAD", W / 2, 900);
  piedino(ctx, W, H);
  return aBlob(canvas);
}
